import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";

import {
  ConfirmDialog,
  Dialog,
  DialogFooter,
  DialogHeader,
  ErrorState,
  ForbiddenState,
  FormField,
  Icon,
  StateMessage,
  Stepper,
} from "@/ds";
import {
  CANCELAR_LABEL,
  CARREGANDO_LABEL,
  CRIAR_LABEL,
  DESTINO_LEGENDA,
  DESTINO_ROTULO,
  ETAPAS_LABEL,
  ORIGEM_LEGENDA,
  ORIGEM_ROTULO,
  PERCENTUAL_AJUDA,
  PROXIMA_LABEL,
  TITULO,
  VOLTAR_LABEL,
} from "./nova-versao-dialog.constants";
import styles from "./nova-versao-dialog.module.scss";
import type { CampoPercentual, NovaVersaoDialogViewModel } from "./types-nova-versao-dialog";

const RODAPE_DIALOGO = { footer: { className: styles.dialogoRodape } };

function Percentual({ campo, desabilitado }: { campo: CampoPercentual; desabilitado: boolean }) {
  return (
    <FormField.Root error={campo.erro}>
      <FormField.Label>{campo.rotulo}</FormField.Label>
      <FormField.Control>
        <InputText
          inputMode="decimal"
          value={campo.valor}
          disabled={desabilitado}
          onChange={(evento) => campo.digitar(evento.target.value)}
          data-testid={campo.testId}
        />
      </FormField.Control>
      <FormField.Error />
    </FormField.Root>
  );
}

function Versao({
  rotulo,
  versao,
  legenda,
  testId,
}: {
  rotulo: string;
  versao: string;
  legenda: string;
  testId: string;
}) {
  return (
    <div className={styles.versao} data-testid={testId}>
      <span className={styles.versaoRotulo}>{rotulo}</span>
      <strong className={styles.versaoNome}>{versao}</strong>
      <span className={styles.versaoLegenda}>{legenda}</span>
    </div>
  );
}

export function NovaVersaoDialogView({
  estado,
  errorMessage,
  recarregar,
  etapa,
  etapas,
  origem,
  destino,
  introducao,
  servicos,
  materiais,
  confirmacao,
  proximaDesabilitada,
  isSubmitting,
  submitError,
  avancar,
  voltar,
  confirmar,
  cancelar,
  descarte,
}: NovaVersaoDialogViewModel) {
  const naConfirmacao = etapa === 2;

  return (
    <>
      <Dialog
        visible
        size="md"
        className={styles.dialogo}
        contentClassName={styles.corpo}
        onHide={cancelar}
        locked={isSubmitting}
        focusOnShow={false}
        header={<DialogHeader title={TITULO} />}
        pt={RODAPE_DIALOGO}
        footer={
          <DialogFooter
            className={styles.rodape}
            cancelLabel={naConfirmacao ? VOLTAR_LABEL : CANCELAR_LABEL}
            confirmLabel={naConfirmacao ? CRIAR_LABEL : PROXIMA_LABEL}
            confirmButtonProps={{
              disabled: naConfirmacao ? estado !== "pronto" : proximaDesabilitada,
            }}
            loading={isSubmitting}
            locked={isSubmitting}
            onCancel={naConfirmacao ? voltar : cancelar}
            onConfirm={naConfirmacao ? confirmar : avancar}
            testId="nova-versao"
          />
        }
        data-testid="nova-versao-dialog"
      >
        <div className={styles.conteudo}>
          {estado === "carregando" ? (
            <StateMessage variant="loading" title={CARREGANDO_LABEL} testId="nova-versao-loading" />
          ) : null}
          {estado === "proibido" ? (
            <ForbiddenState description={errorMessage ?? undefined} testId="nova-versao-proibido" />
          ) : null}
          {estado === "erro" ? (
            <ErrorState
              message={errorMessage ?? undefined}
              onRetry={recarregar}
              testId="nova-versao-erro"
            />
          ) : null}

          {estado === "pronto" ? (
            <>
              <Stepper.Root aria-label={ETAPAS_LABEL}>
                {etapas.map(({ numero, titulo, situacao, ariaLabel, testId, onSelect }) => (
                  <Stepper.Step
                    key={numero}
                    state={situacao}
                    number={numero}
                    title={titulo}
                    ariaLabel={ariaLabel}
                    onSelect={onSelect}
                    testId={testId}
                  />
                ))}
              </Stepper.Root>

              <p className={styles.texto} data-testid="nova-versao-texto">
                {naConfirmacao ? confirmacao.texto : introducao}
              </p>

              <div className={styles.versoes}>
                <Versao
                  rotulo={ORIGEM_ROTULO}
                  versao={origem}
                  legenda={ORIGEM_LEGENDA}
                  testId="nova-versao-origem"
                />
                <Icon name="chevron-right" size={18} />
                <Versao
                  rotulo={DESTINO_ROTULO}
                  versao={destino}
                  legenda={DESTINO_LEGENDA}
                  testId="nova-versao-destino"
                />
              </div>

              {naConfirmacao ? (
                <section className={styles.copiado} data-testid="nova-versao-copiado">
                  <h4 className={styles.copiadoTitulo}>{confirmacao.tituloItens}</h4>
                  <p className={styles.quantidade} data-testid="nova-versao-quantidade">
                    {confirmacao.escoposACopiar}
                  </p>
                  <ul className={styles.itens}>
                    {confirmacao.itens.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </section>
              ) : (
                <>
                  <div className={styles.campos}>
                    <Percentual campo={servicos} desabilitado={isSubmitting} />
                    <Percentual campo={materiais} desabilitado={isSubmitting} />
                  </div>
                  <p className={styles.ajuda}>{PERCENTUAL_AJUDA}</p>
                </>
              )}

              {submitError ? (
                <Message
                  className="p-message-block"
                  severity="error"
                  text={submitError}
                  data-testid="nova-versao-submit-error"
                />
              ) : null}
            </>
          ) : null}
        </div>
      </Dialog>
      {descarte ? <ConfirmDialog {...descarte} /> : null}
    </>
  );
}
